import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Dialog, DialogContent, DialogDescription,
    DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
    Clock, Eye, ArrowDownUp, ShoppingBag, Landmark,
    Receipt, CreditCard, RotateCcw, ArrowLeftRight,
} from 'lucide-react';
import {
    cardAdminApi,
    CardTransactionItem,
    CardTransactionType,
    CardTransactionStatusType,
} from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

const formatUSD = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '—';

const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// ── Type styling ──────────────────────────────────────────────
const TYPE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string; bg: string; border: string }> = {
    purchase: { label: 'Acha', icon: ShoppingBag, color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
    atm: { label: 'ATM', icon: Landmark, color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
    fee: { label: 'Frè', icon: Receipt, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
    recharge: { label: 'Rechaj', icon: CreditCard, color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
    refund: { label: 'Ranbousman', icon: RotateCcw, color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200' },
    reversal: { label: 'Renvèsman', icon: ArrowLeftRight, color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
};

// ── Status styling ─────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
    completed: { label: 'Konplete', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    pending: { label: 'An atant', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    declined: { label: 'Refize', color: 'bg-red-50 text-red-600 border-red-200' },
    reversed: { label: 'Renvèse', color: 'bg-violet-50 text-violet-700 border-violet-200' },
};

// ── Filter tabs ────────────────────────────────────────────────
const TYPE_FILTERS: { key: CardTransactionType | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout' },
    { key: 'purchase', label: 'Acha' },
    { key: 'atm', label: 'ATM' },
    { key: 'fee', label: 'Frè' },
    { key: 'recharge', label: 'Rechaj' },
    { key: 'refund', label: 'Ranbousman' },
];

const STATUS_FILTERS: { key: CardTransactionStatusType | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout Estati' },
    { key: 'completed', label: 'Konplete' },
    { key: 'pending', label: 'An atant' },
    { key: 'declined', label: 'Refize' },
    { key: 'reversed', label: 'Renvèse' },
];

export default function VisaTransactionsPage() {
    const { toast } = useToast();

    const [transactions, setTransactions] = useState<CardTransactionItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [typeFilter, setTypeFilter] = useState<CardTransactionType | 'all'>('all');
    const [statusFilter, setStatusFilter] = useState<CardTransactionStatusType | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const LIMIT = 20;

    const [selected, setSelected] = useState<CardTransactionItem | null>(null);

    const loadTransactions = useCallback(async () => {
        try {
            setLoading(true);
            const res = await cardAdminApi.listCardTransactions({
                page, limit: LIMIT,
                type: typeFilter,
                status: statusFilter,
                search: searchTerm || undefined,
            });
            setTransactions(Array.isArray(res?.results) ? res.results : []);
            setTotal(res?.total ?? 0);
        } catch {
            setTransactions([]);
            toast({ title: 'Erè', description: 'Pa kapab chaje tranzaksyon yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, typeFilter, statusFilter, searchTerm]);

    useEffect(() => { loadTransactions(); }, [loadTransactions]);
    useEffect(() => { setPage(1); }, [typeFilter, statusFilter, searchTerm]);

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));

    const getTypeIcon = (type: string) => {
        const cfg = TYPE_CONFIG[type];
        if (!cfg) return <ArrowDownUp className="h-4 w-4 text-slate-400" />;
        const Icon = cfg.icon;
        return <Icon className={`h-4 w-4 ${cfg.color}`} />;
    };

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Tranzaksyon</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Tranzaksyon Kat</h1>
                            <p className="text-slate-500 text-sm mt-1">Tout tranzaksyon kat (acha, rechaj, frè, ATM, ranbousman).</p>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2 border-slate-200 self-start" onClick={loadTransactions}>
                            <RefreshCw className="h-4 w-4" /> Aktyalize
                        </Button>
                    </div>

                    {/* Stats summary */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="border-none shadow-sm bg-gradient-to-br from-primary-600 to-primary-700 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-primary-100 text-xs font-semibold uppercase">Total</p>
                                <p className="text-3xl font-bold mt-1">{total.toLocaleString()}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Konplete</p>
                                <p className="text-3xl font-bold mt-1 text-emerald-600">
                                    {transactions.filter(t => t.status === 'completed').length}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">An Atant</p>
                                <p className="text-3xl font-bold mt-1 text-amber-600">
                                    {transactions.filter(t => t.status === 'pending').length}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Refize</p>
                                <p className="text-3xl font-bold mt-1 text-red-600">
                                    {transactions.filter(t => t.status === 'declined').length}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Type tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
                        {TYPE_FILTERS.map(({ key, label }) => (
                            <button key={key} onClick={() => setTypeFilter(key as CardTransactionType | 'all')}
                                className={cn('flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    typeFilter === key ? 'bg-primary-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >{label}</button>
                        ))}
                    </div>

                    {/* Status + Search row */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-lg border border-slate-200 overflow-x-auto">
                            {STATUS_FILTERS.map(({ key, label }) => (
                                <button key={key} onClick={() => setStatusFilter(key as CardTransactionStatusType | 'all')}
                                    className={cn('flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap',
                                        statusFilter === key ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-700'
                                    )}
                                >{label}</button>
                            ))}
                        </div>
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                            <Input
                                placeholder="Chèche pa machann, deskripsyon, oswa ID founisè..."
                                className="pl-10 h-10 border-slate-200 rounded-xl"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Table */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Titilè / Kat</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Tip</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Montan</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Machann</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Estati</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Dat</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Aksyon</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr><td colSpan={7} className="px-6 py-16 text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                            <p className="text-slate-500 mt-3 text-sm">Ap chaje tranzaksyon yo...</p>
                                        </td></tr>
                                    ) : transactions.length === 0 ? (
                                        <tr><td colSpan={7} className="px-6 py-16 text-center">
                                            <ArrowDownUp className="h-10 w-10 text-slate-200 mx-auto" />
                                            <p className="text-slate-500 mt-3 font-medium">Okenn tranzaksyon jwenn</p>
                                        </td></tr>
                                    ) : transactions.map((tx) => {
                                        const typeCfg = TYPE_CONFIG[tx.type];
                                        const statusCfg = STATUS_CONFIG[tx.status];
                                        return (
                                            <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-bold text-slate-900">{tx.holder?.fullName ?? 'Titilè'}</p>
                                                    <p className="text-xs text-slate-400 font-mono mt-0.5">•••• {tx.holder?.lastFour ?? '????'}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        {getTypeIcon(tx.type)}
                                                        <Badge variant="outline" className={cn('text-xs font-semibold', typeCfg?.bg, typeCfg?.color, typeCfg?.border)}>
                                                            {typeCfg?.label ?? tx.type}
                                                        </Badge>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm font-bold text-slate-900">{formatUSD(tx.amount)}</p>
                                                    <p className="text-xs text-slate-400">{tx.currency}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm text-slate-700 max-w-[160px] truncate">{tx.merchantName || '—'}</p>
                                                    {tx.merchantCategory && (
                                                        <p className="text-xs text-slate-400 font-mono">{tx.merchantCategory}</p>
                                                    )}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <Badge variant="outline" className={cn('text-xs font-semibold', statusCfg?.color ?? 'bg-slate-50 text-slate-500')}>
                                                        {statusCfg?.label ?? tx.status}
                                                    </Badge>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {formatDate(tx.transactedAt || tx.createdAt)}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelected(tx)}>
                                                        <Eye className="h-4 w-4 text-slate-400" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-sm text-slate-500">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} sou {total}</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                                    <span className="text-sm font-medium">{page}</span>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Detail Modal */}
            <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
                <DialogContent className="sm:max-w-lg">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    {getTypeIcon(selected.type)}
                                    Tranzaksyon — Detay
                                </DialogTitle>
                                <DialogDescription className="flex items-center gap-2">
                                    <Badge variant="outline" className={cn('text-xs', TYPE_CONFIG[selected.type]?.bg, TYPE_CONFIG[selected.type]?.color, TYPE_CONFIG[selected.type]?.border)}>
                                        {TYPE_CONFIG[selected.type]?.label ?? selected.type}
                                    </Badge>
                                    <Badge variant="outline" className={cn('text-xs', STATUS_CONFIG[selected.status]?.color)}>
                                        {STATUS_CONFIG[selected.status]?.label ?? selected.status}
                                    </Badge>
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 py-2 text-sm">
                                {[
                                    { l: 'ID', v: selected.id, mono: true },
                                    { l: 'Kat', v: `•••• ${selected.holder?.lastFour ?? '????'}` },
                                    { l: 'Titilè', v: selected.holder?.fullName ?? '—' },
                                    { l: 'Montan', v: `${formatUSD(selected.amount)} ${selected.currency}` },
                                    { l: 'Machann', v: selected.merchantName ?? '—' },
                                    { l: 'Kategori', v: selected.merchantCategory ?? '—' },
                                    { l: 'Ref Founisè', v: selected.providerTxId, mono: true },
                                    { l: 'Tip Biznis', v: selected.businessType != null ? String(selected.businessType) : '—' },
                                    { l: 'Dat Tranzaksyon', v: formatDate(selected.transactedAt) },
                                    { l: 'Dat Kreyasyon', v: formatDate(selected.createdAt) },
                                ].map(({ l, v, mono }) => (
                                    <div key={l}>
                                        <p className="text-xs text-slate-400">{l}</p>
                                        <p className={cn('font-semibold text-slate-800 mt-0.5', mono ? 'font-mono text-xs break-all' : '')}>{v ?? '—'}</p>
                                    </div>
                                ))}
                                {selected.description && (
                                    <div className="col-span-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
                                        <p className="text-xs text-slate-500 font-bold uppercase">Deskripsyon</p>
                                        <p className="text-sm text-slate-700 mt-1">{selected.description}</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
